import { useState } from 'react'

import { useSaveSettings, useSettings } from '../../hooks/queries'

/**
 * Wie großzügig aus einer Aufzeichnung Vorschläge werden.
 *
 * **Diese zwei Zahlen gab es im Server von Anfang an, in der Oberfläche
 * nicht** — und die Installationsanleitung behauptete trotzdem, sie seien
 * hier einstellbar. Wer also mit „keine längeren Aufenthalte" dastand, konnte
 * nichts dagegen tun, ohne die Schnittstelle von Hand aufzurufen. Aus dem
 * Betrieb gemeldet.
 *
 * Eigene Karte und nicht bei den Bildern: die dort haben eine Decke in der
 * `.env` (sie schützen das Hosting-Paket), diese beiden haben keine. Eine
 * Suchweite kostet nichts — sie entscheidet nur, was als Aufenthalt gilt.
 */
export function ErkennungCard() {
  const settings = useSettings()
  const save = useSaveSettings()
  const [entwurf, setEntwurf] = useState<{ radius?: string; minuten?: string }>({})
  const [gemeldet, setGemeldet] = useState(false)

  const daten = settings.data
  // Ohne Decke ist dieses Konto keine Verwaltung -- der Server hat entschieden.
  if (!daten?.ceiling || !daten.limits) return null

  const werte = daten.settings
  const grenzen = daten.limits

  const radius = entwurf.radius ?? String(werte.stop_radius_m)
  const minuten = entwurf.minuten ?? String(werte.stop_minutes)

  function geaendert(): { stop_radius_m?: number; stop_minutes?: number } {
    const teil: { stop_radius_m?: number; stop_minutes?: number } = {}
    const r = Number(radius)
    const m = Number(minuten)
    if (Number.isInteger(r) && r !== werte.stop_radius_m) teil.stop_radius_m = r
    if (Number.isInteger(m) && m !== werte.stop_minutes) teil.stop_minutes = m
    return teil
  }

  const offen = Object.keys(geaendert()).length > 0

  return (
    <div className="card" style={{ margin: '0 auto 16px', boxShadow: 'none' }}>
      <h1 style={{ fontSize: 17 }}>Aufenthalte erkennen</h1>
      <p className="card__lead">
        Beim Importieren einer Route sucht die Anwendung, wo jemand länger
        geblieben ist, und legt daraus Vorschläge an. Die Zahlen wirken beim
        nächsten Import — und beim Knopf <em>Aufenthalte in der Route suchen</em>{' '}
        auf der Karte.
      </p>

      <label className="field" style={{ marginBottom: 12 }}>
        <span className="field__label">Mindestdauer (Minuten)</span>
        <input
          className="field__input"
          type="number"
          inputMode="numeric"
          name="stop_minutes"
          value={minuten}
          min={grenzen.stop_minutes.min}
          max={grenzen.stop_minutes.max}
          onChange={(e) => setEntwurf({ ...entwurf, minuten: e.target.value })}
        />
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          <strong>0 schaltet die Erkennung ab.</strong> Steht in der Karte nach
          einem Import „längster Halt: 22 Min.", ist hier die Antwort.
        </span>
      </label>

      <label className="field" style={{ marginBottom: 12 }}>
        <span className="field__label">Umkreis (Meter)</span>
        <input
          className="field__input"
          type="number"
          inputMode="numeric"
          name="stop_radius_m"
          value={radius}
          min={grenzen.stop_radius_m.min}
          max={grenzen.stop_radius_m.max}
          onChange={(e) => setEntwurf({ ...entwurf, radius: e.target.value })}
        />
        {/* Aus dem Betrieb, und es ist die überraschende Richtung: mehr ist
            hier nicht besser. */}
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          800 m treffen Dorf, Aussichtspunkt samt Parkplatz und Mittagspause.
          Zwei Kilometer wären die naheliegende Wahl und die schlechtere: damit
          verschmelzen zwei Stopps in einer Stadt zu einem, und eine langsame
          Stadtdurchfahrt sieht aus wie ein Aufenthalt.
        </span>
      </label>

      {save.isError && (
        <p className="notice">
          {save.error instanceof Error ? save.error.message : 'Das hat nicht geklappt.'}
        </p>
      )}
      {gemeldet && !save.isPending && (
        <p className="card__lead" style={{ color: 'var(--accent)' }}>Gespeichert.</p>
      )}

      <button
        className="button button--outline"
        type="button"
        disabled={!offen || save.isPending}
        onClick={() => {
          setGemeldet(false)
          save.mutate(geaendert(), {
            onSuccess: () => {
              setEntwurf({})
              setGemeldet(true)
            },
          })
        }}
      >
        {save.isPending ? 'Einen Moment …' : 'Speichern'}
      </button>
    </div>
  )
}
